import React, { useEffect, useState } from 'react'
import { Formik } from 'formik'
import * as Yup from 'yup'
import { Accordion, AccordionSummary, AccordionDetails } from '@mui/material'
import Tooltip from '@mui/material/Tooltip'
import { t } from '../../i18n'
import Icon from '../shared/icon'
import { CredentialField } from '../organizations/OrderProviderForm'

interface OrderProvider {
  id?: number
  provider_type: string
  credentials: Record<string, string>
  connected: boolean
  destroyable: boolean
  _destroy?: boolean
}

interface OrderProviderLocationFormProps {
  orderProvider: OrderProvider
  requiredFields: CredentialField[]
  isSubmitted: boolean
  setIsSubmitted: (isSubmitted: boolean) => void
  onSubmit: (data: OrderProvider) => void
  onCancel: () => void
}

const OrderProviderLocationForm: React.FC<OrderProviderLocationFormProps> = ({
  orderProvider,
  requiredFields,
  onSubmit,
  isSubmitted,
  setIsSubmitted,
  onCancel
}) => {

  const validationSchema = Yup.object(
    requiredFields.reduce((schema, { name }) => {
      schema[name] = Yup.string().required(t(`order_providers.${name}_required`))
      return schema
    }, {} as Record<string, Yup.StringSchema>)
  )

  const initialValues = requiredFields.reduce((values, { name }) => {
    values[name] = orderProvider.credentials[name] ?? ''
    return values
  }, {} as Record<string, string>)


  const renderSubmittedIcon = () => {
    if(!isSubmitted) {
      return null
    }

    return (
      <Icon className="order-provider-valid-icon me-2" name="success" />
    )
  }

  return (
    <Accordion defaultExpanded className="mb-4">
      <AccordionSummary expandIcon={<Icon name="arrowDown" color="white" />}>
        <h5 style={{ marginBottom: 0 }}>
          {t('order_providers.set_up', { provider_type: orderProvider.provider_type })}
        </h5>
      </AccordionSummary>
      <AccordionDetails>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          enableReinitialize={true}
          validateOnChange={true}
          validateOnBlur={true}
          onSubmit={(values, { setSubmitting }) => {
            const updatedProvider = {
              ...orderProvider,
              credentials: values
            }
            onSubmit(updatedProvider)
            setIsSubmitted(true)
            setSubmitting(false)
          }}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting
          }) => {

            useEffect(() => {
              if (Object.keys(errors).length > 0 && isSubmitted) {
                setIsSubmitted(false)
              }
            }, [errors, isSubmitted])

            return (
              <div>
                {requiredFields.map(({ name, sensitive }) => (
                  <div className="form-group" key={name}>
                    <label htmlFor={name}>{t(`order_providers.${name}`)}</label>
                    <input
                      id={name}
                      name={name}
                      type={sensitive ? 'password' : 'text'}
                      autoComplete={sensitive ? 'new-password' : 'off'}
                      value={values[name]}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={`form-control ${
                        errors[name] && touched[name] ? 'is-invalid' : ''
                      }`}
                    />
                    {errors[name] && touched[name] && (
                      <div className="invalid-feedback">{errors[name]}</div>
                    )}
                  </div>
                ))}

                <div className="form-actions d-flex justify-content-end">
                  {
                    isSubmitted &&
                    <div className="me-3" style={{display: 'flex', alignItems: 'center'}}>
                      {renderSubmittedIcon()} Form submitted
                    </div>
                  }
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => handleSubmit()}
                    disabled={isSubmitting}
                  >
                    Submit
                  </button>
                </div>
              </div>
            )
          }}
        </Formik>
      </AccordionDetails>
    </Accordion>
  )
}

export default OrderProviderLocationForm
